import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import { Card, Button, StatusBadge, CourierDetailContent } from '@/shared/ui'
import { USER_ROLE } from '@/shared/api'
import { useUpdateUserRole } from '@/shared/hooks'
import type { UserDetailsResponse, ModerationHistoryItem } from '@/shared/api'

interface CourierDetailModalProps {
  isOpen: boolean
  onClose: () => void
  courier: UserDetailsResponse | null
  loading?: boolean
  history?: ModerationHistoryItem[] | undefined
  historyLoading?: boolean | undefined
}

export const CourierDetailModal: React.FC<CourierDetailModalProps> = ({
  isOpen,
  onClose,
  courier,
  loading = false,
  history,
  historyLoading = false
}) => {
  const updateRole = useUpdateUserRole()
  const [selectedRole, setSelectedRole] = useState<number>(USER_ROLE.COURIER)

  useEffect(() => {
    if (
      courier &&
      (courier.role === USER_ROLE.COURIER || courier.role === USER_ROLE.EXTERNAL_COURIER)
    ) {
      setSelectedRole(courier.role)
    }
  }, [courier])

  if (!isOpen) return null

  const handleRoleChange = async () => {
    if (!courier) return
    try {
      await updateRole.mutateAsync({
        userId: courier.user_id,
        request: { role: selectedRole },
      })
    } catch (error) {
      // Ошибка уже обработана в хуке (включая 400 — активные заказы)
    }
  }

  // Карточка «Тип курьера» — это действие, а не данные: остаётся в модалке
  // и передаётся в слот общего контента (после истории, перед личными данными).
  const roleCard = courier &&
    (courier.role === USER_ROLE.COURIER || courier.role === USER_ROLE.EXTERNAL_COURIER) ? (
    <Card>
      <div className="p-5">
        <h4 className="text-base font-medium text-gray-900 mb-4 flex items-center">
          <TruckIcon className="w-4 h-4 mr-2" />
          Тип курьера
        </h4>
        <div className="flex flex-wrap items-center gap-4">
          <StatusBadge status={courier.role} type="role" />
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(Number(e.target.value))}
            className="admin-input w-48"
          >
            <option value={USER_ROLE.COURIER}>Штатный</option>
            <option value={USER_ROLE.EXTERNAL_COURIER}>Сторонний</option>
          </select>
          <Button
            size="sm"
            variant="primary"
            onClick={handleRoleChange}
            isLoading={updateRole.isPending}
            disabled={updateRole.isPending || selectedRole === courier.role}
          >
            Изменить тип
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Смена типа недоступна, если у курьера есть активные заказы
        </p>
      </div>
    </Card>
  ) : null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Детальная информация о курьере
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
              <span className="ml-3 text-gray-600">Загрузка...</span>
            </div>
          ) : courier ? (
            <>
              {/* Header with Name */}
              <div className="text-center border-b pb-4 mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {courier.full_name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {courier.role_text} • {courier.status_text}
                </p>
              </div>

              <CourierDetailContent
                courier={courier}
                history={history}
                historyLoading={historyLoading}
              >
                {roleCard}
              </CourierDetailContent>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Информация о курьере недоступна</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  )
}
